import type { DepartureDto } from "../dtos/traveler/availability.dto";

/**
 * The style guide's scarcity bands, read by every surface that labels a
 * departure: the tour page's departure list and package cards, and the
 * Departure Dates block. One copy, so the same seat count never wears two
 * levels of urgency on a block and on the tour page it links to.
 *
 *     spots === 0  → "Sold out"
 *     spots <= 2   → "Almost sold out · N spots left"
 *     spots <= 5   → "Filling up fast · N spots left"
 *     otherwise    → "Available" (no count)
 *
 * The cut-offs are the guide's, not ours; no band the guide does not define
 * is added.
 */
export const ALMOST_SOLD_OUT_AT = 2;
export const FILLING_UP_AT = 5;

/**
 * A departure is open when the operator has not closed it AND it is either
 * uncapped or has seats left — the same rule as the tour page's calendar.
 */
export function isDepartureOpen(d: DepartureDto): boolean {
  return !d.isClosed && (d.spotsLeft === null || d.spotsLeft > 0);
}

/**
 * Will this departure run — is there no minimum it can still fall short of?
 *
 *  - sold out or closed → no: a date nobody can join is not a date that runs.
 *  - no minimum set (`minParty` null or 0) → yes: there is no threshold that
 *    could cancel it.
 *  - a minimum is set → has it been reached? `capacity − spotsLeft` is the
 *    seats already booked. With no ceiling (`capacity` null) the bookings
 *    cannot be counted, so the answer is no: a minimum we cannot check is not
 *    one we may claim is met.
 *
 * The ceiling is the row's own `capacity` — on a package-scoped departure,
 * that package's allocation, the pool its `spotsLeft` is counted against.
 */
export function isGuaranteedToRun(
  d: DepartureDto,
  minParty: number | null,
): boolean {
  if (!isDepartureOpen(d)) return false;
  if (minParty === null || minParty <= 0) return true;
  if (d.capacity === null || d.spotsLeft === null) return false;
  return d.capacity - d.spotsLeft >= minParty;
}
